import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, Calendar, Mail, User as UserIcon, RefreshCw, LogOut, ShieldCheck, Sparkles, Loader2, TrendingUp, TrendingDown, Minus, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../context/AuthContext";
import { StoreOrders } from "../components/StoreOrders";
import { ScoreChart } from "../components/ScoreChart";
import { ReferralCard } from "../components/ReferralCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, loading, logout, subscribe, cancelSubscription } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [progress, setProgress] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    axios.get(`${API}/payments/me/subscription`)
      .then(({ data }) => setSub(data))
      .catch(() => {});
    axios.get(`${API}/auth/me/assessments`)
      .then(({ data }) => setProgress(data.funnels))
      .catch(() => {});
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500">Loading…</div>;
  }

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const { url } = await subscribe();
      window.location.href = url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setRenewing(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await cancelSubscription();
      toast.success("Auto-renewal cancelled", { description: res.message });
      setSub((s) => (s ? { ...s, auto_renew: false } : s));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCancelling(false);
    }
  };

  const isPremium = !!sub?.is_premium;
  const daysLeft = sub?.days_remaining;
  const expiresOn = sub?.subscription_expires_at?.slice(0, 10);
  const showSoon = isPremium && daysLeft !== null && daysLeft !== undefined && daysLeft <= 3;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="account-page">
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-1">My Account</h1>
        <p className="text-gray-500 text-sm">Manage your subscription, profile and consent records.</p>
      </div>

      {/* Subscription card */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-8 mb-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${isPremium ? "bg-gradient-to-br from-amber-400 to-amber-600" : "bg-gray-100"}`}>
              <Crown className={`w-6 h-6 ${isPremium ? "text-white" : "text-gray-400"}`} />
            </div>
            <div>
              <p className="text-xs uppercase text-gray-400 tracking-wide font-semibold">Plan</p>
              <h2 className="text-2xl font-bold text-gray-900" data-testid="account-plan-name">
                {isPremium ? "Premium" : "Free"}
              </h2>
            </div>
          </div>
          {isPremium && (
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5" /> Active
            </span>
          )}
        </div>

        {isPremium ? (
          <>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Expires on</p>
                <p className="font-semibold text-gray-900 flex items-center gap-1.5" data-testid="account-expires-on">
                  <Calendar className="w-4 h-4 text-gray-400" /> {expiresOn || "—"}
                </p>
              </div>
              <div className={`rounded-xl p-4 ${showSoon ? "bg-amber-50 border border-amber-200" : "bg-gray-50"}`}>
                <p className="text-xs text-gray-500 mb-1">Days remaining</p>
                <p className={`font-semibold flex items-center gap-1.5 ${showSoon ? "text-amber-800" : "text-gray-900"}`} data-testid="account-days-left">
                  <RefreshCw className="w-4 h-4 text-gray-400" /> {daysLeft ?? "—"} {showSoon && "· renew soon"}
                </p>
              </div>
            </div>

            {sub?.last_payment && (
              <div className="text-xs text-gray-500 border-t border-gray-100 pt-4 mb-6">
                Last payment: £{Number(sub.last_payment.amount).toFixed(2)} on
                {" "}
                {sub.last_payment.fulfilled_at ? new Date(sub.last_payment.fulfilled_at).toLocaleDateString() : "—"}
              </div>
            )}

            {sub?.auto_renew ? (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="auto-renew-active">
                  <RefreshCw className="w-4 h-4" /> Auto-renewal is <b>on</b> — £4.99 charged monthly, no action needed.
                </div>
                <Button
                  onClick={handleCancel}
                  disabled={cancelling}
                  variant="outline"
                  data-testid="account-cancel-sub-btn"
                  className="w-full h-11 rounded-full border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel auto-renewal"}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  If you cancel, Premium stays active until {expiresOn || "your period ends"}.
                </p>
              </>
            ) : (
              <>
                <Button
                  onClick={handleRenew}
                  disabled={renewing}
                  data-testid="account-renew-btn"
                  className="w-full h-12 rounded-full bg-gradient-to-r from-amber-500 to-amber-700 text-white font-semibold shadow-lg shadow-amber-500/30"
                >
                  {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="flex items-center justify-center gap-2">
                      <Sparkles className="w-4 h-4" /> Subscribe — £4.99/mo auto-renewing
                    </span>
                  )}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Auto-renews monthly via Stripe. Cancel anytime — access runs to the end of the paid period.
                </p>
              </>
            )}
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              Upgrade to <b>Premium</b> to unlock the Men's Vitality Check and Women's Hormone
              Assessment scorecards.
            </p>
            <Button
              onClick={handleRenew}
              disabled={renewing}
              data-testid="account-upgrade-btn"
              className="w-full h-12 rounded-full bg-gradient-to-r from-amber-500 to-amber-700 text-white font-semibold shadow-lg"
            >
              {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4" /> Subscribe — £4.99/mo
                </span>
              )}
            </Button>
          </>
        )}
      </div>

      <ReferralCard />

      {/* Assessment progress */}
      {progress && Object.keys(progress).length > 0 && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 mb-6" data-testid="progress-section">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">My Progress</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(progress).map(([funnel, f]) => {
              const label = funnel === "men" ? "Men's Vitality Check" : funnel === "women" ? "Women's Hormone Assessment" : funnel;
              const route = funnel === "men" ? "/mens-scorecard" : "/womens-assessment";
              const change = f.score_change;
              return (
                <div key={funnel} className="border border-gray-200 rounded-2xl p-5" data-testid={`progress-${funnel}`}>
                  <p className="font-semibold text-gray-900 mb-1">{label}</p>
                  <p className="text-xs text-gray-400 mb-3">Last taken {f.last_taken_at?.slice(0, 10)} · {f.history.length}× total</p>
                  <div className="flex items-end gap-3 mb-3">
                    <span className="text-3xl font-bold text-gray-900" data-testid={`progress-score-${funnel}`}>{f.last_score}<span className="text-sm text-gray-400 font-normal">/30</span></span>
                    {change !== null && change !== undefined && (
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border mb-1 ${change < 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : change > 0 ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-gray-50 border-gray-200 text-gray-500"}`} data-testid={`progress-change-${funnel}`}>
                        {change < 0 ? <TrendingDown className="w-3 h-3" /> : change > 0 ? <TrendingUp className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                        {change > 0 ? `+${change}` : change} vs previous
                      </span>
                    )}
                  </div>
                  {f.history.length >= 2 && (
                    <div className="mb-3" data-testid={`progress-chart-${funnel}`}>
                      <ScoreChart history={f.history} />
                    </div>
                  )}
                  {f.can_retake ? (
                    <Button size="sm" onClick={() => navigate(route)} className="rounded-full w-full" data-testid={`retake-btn-${funnel}`}>
                      Retake assessment now
                    </Button>
                  ) : (
                    <p className="text-xs text-gray-500 bg-gray-50 rounded-full px-3 py-2 text-center" data-testid={`retake-wait-${funnel}`}>
                      Retake available in {f.days_until_retake} day{f.days_until_retake === 1 ? "" : "s"} ({f.retake_available_at?.slice(0, 10)})
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-4">We recommend re-assessing every 6 weeks — enough time for herbal protocols to show measurable change. Lower scores mean fewer symptoms.</p>
        </div>
      )}

      {/* Store orders */}
      <StoreOrders />

      {/* Profile card */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500 w-24">Name</span>
            <span className="text-gray-900 font-medium" data-testid="account-name">{user.full_name || user.first_name || "—"}</span>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500 w-24">Email</span>
            <span className="text-gray-900 font-medium" data-testid="account-email">{user.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500 w-24">Agreement</span>
            <span className="text-gray-900 font-medium" data-testid="account-agreement-status">
              {user.agreement_signed
                ? `Signed ${user.agreement_signed_at?.slice(0,10)}`
                : "Not signed"}
            </span>
          </div>
        </div>
      </div>

      <Button
        onClick={() => { logout(); navigate("/"); }}
        data-testid="account-logout-btn"
        variant="outline"
        className="w-full h-11 rounded-full border-rose-200 text-rose-700 hover:bg-rose-50 font-semibold flex items-center justify-center gap-2"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </Button>
    </div>
  );
}
